import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { updateRomaneio, getRomaneioData } from '../../services/Romaneio/romaneioService';
import useUserRoles from '../../hooks/Home/useUserRoles';
import ErrorModal from '../Modals/ErrorModal';
import SuccessModal from '../Modals/SuccessModal';
import { usePermissions } from '../../hooks/usePermissions';
import DriverInput from '../Utils/DriverInput';
import DateInput from '../Utils/DateInput';
import HourInput from '../Utils/HourInput';
import HarvestInput from '../Utils/HarvestInput';
import PersonInput from '../Utils/PersonInput';
import LotInput from '../Utils/LotInput';
import FarmInput from '../Utils/FarmInput';
import SiloInput from '../Utils/SiloInput';
import ProductInput from '../Utils/ProductInput';
import BalanceInput from '../Utils/BalanceInput';
import InputField from '../Utils/InputField';
import TextAreaInput from '../Utils/TextAreaInput';
import WeightInput from '../Utils/WeightInput';
import DiscountInput from '../Utils/DiscountInput';
import DecimalInput from '../Utils/DecimalInput';
import useFormatRomaneioPayload from '../../hooks/Romaneio/useFormatRomaneioPayload';
import useDiscountCalculation from "../../hooks/Romaneio/useDiscountCalculation";
import usePopulateRomaneioForm from '../../hooks/Romaneio/usePopulateRomaneioForm';

const EditRomaneioModal = ({ show, handleClose, romaneioId, refreshRomaneios }) => {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('romaneioDelete');
  const { register, handleSubmit, formState: { errors }, reset, setValue, control, watch, trigger } = useForm({
    defaultValues: {
      entry: false,
      exit: false,
      grossWeight: false,
      netWeight: true,
      reactivationForbidden: false,
    }
  });
  const userRoles = useUserRoles();
  const hasAdminRole = userRoles.includes('ADMIN');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [inactiveLoadedFromServer, setInactiveLoadedFromServer] = useState(false);
  const selectedProductId = watch("productId");
  const reactivationForbidden = watch("reactivationForbidden");
  const { formatPayload } = useFormatRomaneioPayload();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // eslint-disable-next-line
  const [isReadOnlyInputs, setIsReadOnlyInputs] = useState([]);
  
  const { errorMessage: fetchErrorMessage, showErrorModal: fetchShowErrorModal } =
    usePopulateRomaneioForm(show, romaneioId, setValue, reset, isReadOnlyInputs);

  useEffect(() => {
    if (fetchErrorMessage) {
      setErrorMessage(fetchErrorMessage);
      setShowErrorModal(fetchShowErrorModal);
    }
  }, [fetchErrorMessage, fetchShowErrorModal]);

  useEffect(() => {
    const checkIfFinished = async () => {
      if (show && romaneioId) {
        try {
          const data = await getRomaneioData(romaneioId);
          const finished = data?.balance !== null && data?.balance !== undefined && 
                          data?.tare !== null && data?.tare !== undefined;
          setIsFinished(finished);
          setInactiveLoadedFromServer(data?.active === false);
        } catch (error) {
          setIsFinished(false);
          setInactiveLoadedFromServer(false);
        }
      } else {
        setIsFinished(false);
        setInactiveLoadedFromServer(false);
      }
    };
    checkIfFinished();
  }, [show, romaneioId]);

  useDiscountCalculation(control, setValue, watch("discountId"));

  const onSubmit = async (data) => {
    if (inactiveLoadedFromServer) return;
    if (isFinished && !hasAdminRole) return;
    setIsSubmitting(true);
    try {
      const formattedData = formatPayload(data);
      
      await updateRomaneio(romaneioId, formattedData);
      setSuccessMessage("Romaneio atualizado com sucesso!");
      setShowSuccessModal(true);
      reset();
    } catch (error) {
      setErrorMessage(error.message || "Erro ao atualizar romaneio.");
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBalanceOrTareChange = (field, value) => {
    setValue(field, value);
  
    const balance = watch("balance");
    const tare = watch("tare");
  
    if (balance && tare) {
      setValue("entryDate", new Date().toISOString().split("T")[0]);
      setValue("entryHour", new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }));
    }
  };  

  const handleCloseErrorModal = () => setShowErrorModal(false);
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    refreshRomaneios();
    handleClose();
  };

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show, reset]);

  useEffect(() => {
      setValue('discountId', null);
    }, [selectedProductId, setValue]);

  return (
    <>
      <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={false} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Editar Romaneio <b>#{watch("oldId")}</b></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={2}>
                <Controller
                  name="entryDate"
                  control={control}
                  rules={{ required: 'Data de entrada é obrigatória' }}
                  render={({ field, fieldState }) => {
                    return (
                      <DateInput
                        isEditing = { true }
                        label="Data"
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error}
                        placeholder="__/__/____"
                        userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.entryDate ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={1}>
                <Controller
                  name="entryHour"
                  control={control}
                  rules={{ required: 'Hora de entrada é obrigatória' }}
                  render={({ field, fieldState }) => {
                    return (
                      <HourInput
                        label="Hora"
                        name="entryHour"
                        placeholder="__:__"
                        control={{ register, setValue, watch }}
                        errors={errors}
                        userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.entryHour ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={2}>
                <div className='mb-3'>
                  <p className="mb-0">Tipo</p>
                  {['entry', 'exit'].map((field) => (
                    <Form.Group controlId={field} key={field} style={field === 'entry' ? { marginBottom: '-3px' } : {}}>
                    <Form.Check
                      disabled={isFinished || !hasAdminRole}
                      className='mb-0'
                      type="checkbox"
                      label={field === 'entry' ? 'Entrada' : 'Saída'}
                      {...register(field, {
                      onChange: (e) => {
                        setValue(field === 'entry' ? 'exit' : 'entry', false);
                      },
                      validate: () => {
                        const otherField = field === 'entry' ? 'exit' : 'entry';
                        return watch(field) || watch(otherField) || 'Selecione o tipo do romaneio';
                      },
                      })}
                    />
                    </Form.Group>
                  ))}
                    {errors.entry && ( <p className="text-danger mb-0">{errors.entry.message}</p> )}
                </div>
              </Col>
              <Col md={5} style={{ display: 'none' }}>
                <Controller
                  name="harvestId"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <HarvestInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('harvestId', selected);
                        }}
                        onCreated={(created) => {
                          setValue('harvestId', { id: created.id, name: created.name });
                        }}
                        label="Safra"
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="licensePlate"
                  control={control}
                  rules={{ required: 'A placa é obrigatória.' }}
                  render={({ field, fieldState }) => (
                    <DriverInput
                      value={field.value}
                      label="Placa"
                      isInvalid={!!fieldState.error}
                      editLocked={isFinished}
                      onChange={(selected) => {
                        const plate = selected?.licensePlate || '';
                        const driver = selected?.id ? { id: selected.id, name: selected.name } : null;

                        setValue('licensePlate', plate);
                        setValue('driverId', driver);
                        trigger(['licensePlate', 'driverId']);
                      }}
                    />
                  )}
                />
              </Col>
              <Col md={5}>
                <Controller
                  name="driverId"
                  control={control}
                  rules={{ required: 'Motorista é obrigatório' }}
                  render={({ field, fieldState }) => {
                    return (
                      <PersonInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('driverId', selected);
                          trigger('driverId');
                        }}
                        onCreated={(created) => {
                          setValue('driverId', { id: created.id, name: created.name });
                        }}
                        label="Motorista"
                        isInvalid={!!fieldState.error}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                        type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                      />
                    )
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4} style={{ display: 'none' }}>
                <Controller
                  name="lotId"
                  control={control}
                  rules={{ required: 'Lote é obrigatório' }}
                  render={({ field, fieldState }) => {
                    return (
                      <LotInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selectedLot) => {
                          setValue('lotId', { id: selectedLot?.id, description: selectedLot?.description || '' });
                          if (selectedLot) {
                            
                            if(selectedLot.lot?.farm) {
                              setValue('farmId', { id: selectedLot.lot.farm?.id || null, name: selectedLot.lot.farm?.name || '' });
                            } else {
                              setValue('farmId', null);
                            }

                            if(selectedLot.lot?.person) {
                              setValue('originPersonId', { id: selectedLot.lot.person?.id || null, name: selectedLot.lot.person?.name || '' });
                            } else {
                              setValue('originPersonId', null);
                            }

                            if(selectedLot.lot?.silo) {
                              setValue('siloId', { id: selectedLot.lot.silo?.id || null, name: selectedLot.lot.silo?.name || '' });
                            } else {
                              setValue('siloId', null);
                            }

                            if(selectedLot.lot?.product) {
                              setValue('productId', { id: selectedLot.lot.product?.id || null, description: selectedLot.lot.product?.description || '' });
                            } else {
                              setValue('productId', null);
                            }
                          }
                        }}
                        onCreated={(created) => {
                          setValue('lotId', { id: created.id, name: created.name });
                        }}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                        label="Lote"
                        isInvalid={!!fieldState.error}
                      />
                    )
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Controller
                  name="originPersonId"
                  control={control}
                  rules={{ required: 'Produtor / Cliente é obrigatório' }}
                  render={({ field, fieldState }) => {
                    return (
                      <PersonInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('originPersonId', selected);
                          setValue('ratePerc', selected?.discount);
                          trigger('originPersonId');
                        }}
                        onCreated={(created) => {
                          setValue('originPersonId', { id: created.id, name: created.name });
                          setValue('ratePerc', created?.discount);
                        }}
                        label="Produtor / Cliente"
                        isInvalid={!!fieldState.error}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                        type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                      />
                    );
                  }}
                />
              </Col>
              <Col md={4}>
                <Controller
                  name="productId"
                  control={control}
                  rules={{ required: 'Cultura é obrigatório' }}
                  render={({ field, fieldState }) => {
                    return (
                      <ProductInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('productId', selected);
                          trigger('productId');
                        }}
                        onCreated={(created) => {
                          setValue('productId', { id: created.id, name: created.name });
                        }}
                        label="Cultura"
                        isInvalid={!!fieldState.error}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={2}>
                <InputField
                  label="Número NF"
                  name="numberNF"
                  placeholder="Número NF"
                  register={register}
                  errors={errors}
                  rules={{ required: watch('balance') && watch('tare') ? 'Número NF é obrigatório' : false }}
                  userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.numberNF ? true : false)}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="weightNF"
                  control={control}
                  rules={{
                    required: 'Peso da NF é obrigatório.',
                    validate: (value) => {
                      if (Number(value) <= 0) return 'O peso deve ser maior que zero.';
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso NF"
                      isInvalid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.weightNF ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col md={6} style={{ display: 'none' }}>
                <Controller
                  name="destinyPersonId"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <PersonInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('destinyPersonId', selected);
                          trigger('destinyPersonId');
                        }}
                        onCreated={(created) => {
                          setValue('destinyPersonId', { id: created.id, name: created.name });
                        }}
                        label="Produtor / Cliente / Destino"
                        isInvalid={!!fieldState.error}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                        type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                      />
                    );
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={6} style={{ display: 'none' }}>
                <Controller
                  name="farmId"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FarmInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('farmId', selected);
                        }}
                        onCreated={(created) => {
                          setValue('farmId', { id: created.id, name: created.name });
                        }}
                        label="Fazenda"
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={6} style={{ display: 'none' }}>
                <Controller
                  name="siloId"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <SiloInput
                        value={field.value}
                        createdNew={true}
                        onChange={(selected) => {
                          setValue('siloId', selected);
                        }}
                        onCreated={(created) => {
                          setValue('siloId', { id: created.id, name: created.name });
                        }}
                        label="Silo"
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Controller
                  name="discountId"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <DiscountInput
                        value={field.value}
                        createdNew={hasAdminRole && !isFinished}
                        onChange={(selected) => {
                          setValue('discountId', selected);
                        }}
                        onCreated={(created) => {
                          setValue('discountId', { id: created.id, name: created.name });
                        }}
                        label="Tabela de Descontos"
                        isInvalid={!!fieldState.error}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value?.id ? true : false)}
                        readOnly={isFinished || !watch("productId")}
                        product={watch("productId")}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="balance"
                  control={control}
                  render={({ field }) => {
                    return (
                      <BalanceInput
                        value={field.value}
                        onChange={(value) => handleBalanceOrTareChange("balance", value)}
                        label="Peso Bruto"
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value ? true : false)}
                        btnUserRoles={isFinished ? true : (hasAdminRole ? false : field.value ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="tare"
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <BalanceInput
                        value={field.value}
                        onChange={(value) => handleBalanceOrTareChange("tare", value)}
                        label="Tara"
                        isInvalid={!!fieldState.error}
                        userRoles={isFinished ? true : (hasAdminRole ? false : field.value ? true : false)}
                        btnUserRoles={isFinished ? true : (hasAdminRole ? false : field.value ? true : false)}
                      />
                    )
                  }}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="labelWeightNotDiscount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Líq. s/Desc"
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                      isBold={true}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="labelWeightAndRate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Líq. c/Desc + Tx"
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                      isBold={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={3} style={{ display: 'none' }}>
                <Controller
                  name="exitDate"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DateInput
                      isEditing = { true }
                      label="Data"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error}
                      placeholder="__/__/____"
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.exitDate ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col md={2} style={{ display: 'none' }}>
                <HourInput
                  label="Hora"
                  name="exitHour"
                  placeholder="__:__"
                  control={{ register, setValue, watch }}
                  errors={errors}
                  userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.exitHour ? true : false)}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6} md={2}>
                <Controller
                  name="umidade"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Umidade %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.umidade ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="umidadeWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Umidade"
                      placeholder=""
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="impureza"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Impurezas %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.impureza ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="impurezaWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Impurezas"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="ardido"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Ardidos %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.ardido ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="ardidoWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Ardidos"
                      placeholder=""
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6} md={2}>
                <Controller
                  name="verde"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Verdes %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.verde ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="verdeWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Verdes"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="quebrado"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Quebrados %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.quebrado ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="quebradoWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Quebrados"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="avariado"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Avariados %"
                      placeholder=" "
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.avariado ? true : false)}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col xs={6} md={2}>
                <Controller
                  name="avariadoWeight"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Peso Avariados"
                      isInvalid={!!fieldState.error}
                      placeholder=""
                      readOnly={true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col md={2}>
                <Controller
                  name="ratePerc"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DecimalInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Tx. Serviço %"
                      isInvalid={!!fieldState.error}
                      userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.ratePerc ? true : false)}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="rateKg"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Tx. Serviço Kg"
                      isInvalid={!!fieldState.error}
                      readOnly={isFinished || !hasAdminRole}
                    />
                  )}
                />
              </Col>
              <Col md={2}>
                <Controller
                  name="labelDiscount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <WeightInput
                      value={field.value}
                      onChange={field.onChange}
                      label="Total Descontos"
                      isInvalid={!!fieldState.error}
                      readOnly={true}
                    />
                  )}
                />
              </Col>
              <Col md={6}>
                <TextAreaInput
                  label="Informações Adicionais"
                  name="observations"
                  placeholder="Digite suas observações"
                  rows={1}
                  register={register}
                  rules={{
                    required: false,
                    maxLength: {
                      value: 10000,
                      message: 'Máximo de 10.000 caracteres permitidos',
                    },
                  }}
                  userRoles={isFinished ? true : (hasAdminRole ? false : isReadOnlyInputs.observations ? true : false)}
                  errors={errors}
                />
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Label className="center">Status</Form.Label>
                <Controller
                  name="active"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="checkbox"
                      label="Confirmado"
                      checked={field.value}
                      onChange={field.onChange}
                      disabled={
                        inactiveLoadedFromServer ||
                        (isFinished
                          ? (!hasAdminRole || reactivationForbidden)
                          : (!hasAdminRole || !canDelete))
                      }
                    />
                  )}
                />
              </Col>
            </Row>
            <Button
              variant="primary"
              className="button-custom w-100 mt-3"
              type="submit"
              disabled={isSubmitting || inactiveLoadedFromServer || (isFinished && !hasAdminRole)}
            >
              Atualizar
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ErrorModal
        show={showErrorModal}
        handleClose={handleCloseErrorModal}
        errorMessage={errorMessage}
      />

      <SuccessModal
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
        successMessage={successMessage}
      />
    </>
  );
};

export default EditRomaneioModal;